import { print } from "graphql";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { PublicDocumentView } from "@/app/public/[id]/_page/views/PublicDocumentView";
import { FullPageLoading } from "@/components/atoms/FullPageLoading";
import { Typography } from "@/components/ui/typography";
import { Queries, Values } from "@/constants";
import { idToTypename } from "@/objects/@utils";
import { State } from "@/state";

// The page has no Clerk session and so no Apollo client: the typed documents
// are printed and sent over plain ``fetch``. The base node and its group
// payloads (line items, payment schedule) are separate queries, merged below
// into the one object the view renders.
const PUBLIC_NODE_QUERIES = [
  Queries.PUBLIC_NODE_QUERY,
  Queries.PUBLIC_DOCUMENT_LINE_ITEMS_QUERY,
  Queries.PUBLIC_DOCUMENT_PAYMENT_SCHEDULE_QUERY,
].map((document) => print(document));

const COMPONENTS = {
  PublicDocumentObject: PublicDocumentView,
};

export default function Page() {
  const { id } = useParams<{ id: string }>();
  const typename = idToTypename(id);
  const styleTheme = State((state) => state.styleTheme);
  // Outside the app's ``ThemeRegistry``: the corners chosen, written here.
  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-style-theme", styleTheme);
  }, [styleTheme]);

  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [node, ...groups] = await Promise.all(
        PUBLIC_NODE_QUERIES.map((query) =>
          fetch(Values.URLS["GRAPHQL"], {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query, variables: { id } }),
          })
            .then((res) => res.json())
            .then((json) => json?.data?.node ?? null),
        ),
      );
      setData(node ? Object.assign({}, node, ...groups) : null);
    } catch {
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const View = COMPONENTS?.[typename];

  return (
    <>
      {View && isLoading ? (
        <FullPageLoading />
      ) : View && data ? (
        <View data={data} isLoading={isLoading} refetch={fetchData} />
      ) : (
        // A link to nothing shared: one never shared, no longer shared, or
        // mistyped - said, rather than left loading.
        <div
          data-testid="public-page-not-found"
          className="flex flex-col items-center justify-center gap-2 h-screen px-4 text-center"
        >
          <Typography level="title-lg">No document found</Typography>
          <Typography level="body-md" className="text-muted-foreground">
            This link isn't shared, or no longer is. Ask whoever sent it for a
            new one.
          </Typography>
        </div>
      )}
    </>
  );
}
